import type { StoryBible } from '../types';

export type ForbiddenMatchKind =
  | 'GLOBAL_CLICHE_PATTERN'
  | 'PROJECT_WHOLE_WORD'
  | 'PROJECT_PHRASE'
  | 'DIRECTOR_RESTRICTION';

/** @deprecated Global whole-word bans removed; use GLOBAL_FORBIDDEN_SENSORY_CLICHES instead. */
export const GLOBAL_FORBIDDEN_PROSE_TERMS = [] as const;

export interface ForbiddenPattern {
  id: string;
  label: string;
  pattern: RegExp;
}

export interface ForbiddenProsePolicy {
  globalClichePatterns: ForbiddenPattern[];
  projectForbiddenWholeWords: string[];
  projectForbiddenPhrases: string[];
  directorRestrictions: string[];
}

export interface ForbiddenProseMatch {
  kind: ForbiddenMatchKind;
  matchedRuleId: string;
  term: string;
  count: number;
}

export interface ForbiddenProseEnforcementResult {
  text: string;
  blocked: boolean;
  repaired: boolean;
  remaining: ForbiddenProseMatch[];
  agentStage: string;
}

const GLOBAL_CLICHE_PATTERN_SOURCES: ReadonlyArray<{ id: string; label: string; source: string }> = [
  { id: 'ozone_smell_of', label: 'smell of ozone', source: String.raw`\bsmell\s+of\s+ozone\b` },
  { id: 'ozone_smelled_of', label: 'smelled of ozone', source: String.raw`\bsmelled\s+of\s+ozone\b` },
  { id: 'ozone_smelled_like', label: 'smelled like ozone', source: String.raw`\bsmelled\s+like\s+ozone\b` },
  { id: 'ozone_smelling_of', label: 'smelling of ozone', source: String.raw`\bsmelling\s+of\s+ozone\b` },
  { id: 'ozone_scent_of', label: 'scent of ozone', source: String.raw`\bscent\s+of\s+ozone\b` },
  { id: 'ozone_reek_of', label: 'reek of ozone', source: String.raw`\breek\s+of\s+ozone\b` },
  { id: 'ozone_tang', label: 'ozone tang', source: String.raw`\bozone\s+tang\b` },
  { id: 'ozone_tang_of', label: 'tang of ozone', source: String.raw`\btang\s+of\s+ozone\b` },
  { id: 'ozone_sharpness_of', label: 'sharpness of ozone', source: String.raw`\bsharpness\s+of\s+ozone\b` },
  { id: 'ozone_sharp_air', label: 'ozone-sharp air', source: String.raw`\bozone[- ]sharp\s+air\b` },
  { id: 'ozone_filled_air', label: 'ozone-filled air', source: String.raw`\bozone[- ]filled\s+air\b` },
  { id: 'ozone_air_tasted_of', label: 'air tasted of ozone', source: String.raw`\bair\s+tasted\s+of\s+ozone\b` },
  { id: 'ozone_taste_of', label: 'taste of ozone', source: String.raw`\btaste\s+of\s+ozone\b` },
  { id: 'ozone_air_thick_with', label: 'the air was thick with ozone', source: String.raw`\bthe\s+air\s+was\s+thick\s+with\s+ozone\b` },
  { id: 'ozone_electrical_smell', label: 'electrical ozone smell', source: String.raw`\belectrical\s+ozone\s+smell\b` },
  { id: 'ozone_filled', label: 'ozone-filled', source: String.raw`\bozone[- ]filled\b` },
  { id: 'ozone_tasted_of', label: 'tasted of ozone', source: String.raw`\btasted\s+of\s+ozone\b` },
  { id: 'ionized_air_smell_of', label: 'smell of ionized air', source: String.raw`\bsmell(?:ing|ed)?\s+of\s+ionized\s+air\b` },
  { id: 'ionized_air_tasted_of', label: 'tasted of ionized air', source: String.raw`\btasted\s+of\s+ionized\s+air\b` },
  { id: 'ionized_air_scent_of', label: 'scent of ionized air', source: String.raw`\bscent\s+of\s+ionized\s+air\b` },
  { id: 'ionized_air_smelled_like', label: 'smelled like ionized air', source: String.raw`\bsmelled\s+like\s+ionized\s+air\b` },
];

export const GLOBAL_FORBIDDEN_SENSORY_CLICHES: ForbiddenPattern[] = GLOBAL_CLICHE_PATTERN_SOURCES.map(
  (entry) => ({
    id: entry.id,
    label: entry.label,
    pattern: new RegExp(entry.source, 'gi'),
  }),
);

const CLICHE_REPLACEMENTS: ReadonlyArray<{ pattern: RegExp; replacement: string }> = [
  { pattern: /\bsmell(?:ing|ed)?\s+of\s+ozone\b/gi, replacement: 'carrying the reek of scorched insulation' },
  { pattern: /\bsmelled\s+of\s+ozone\b/gi, replacement: 'reeked of scorched insulation' },
  { pattern: /\bsmelled\s+like\s+ozone\b/gi, replacement: 'reeked of scorched insulation' },
  { pattern: /\bsmelling\s+of\s+ozone\b/gi, replacement: 'carrying the reek of scorched insulation' },
  { pattern: /\bscent\s+of\s+ozone\b/gi, replacement: 'reek of electrical smoke' },
  { pattern: /\breek\s+of\s+ozone\b/gi, replacement: 'reek of scorched insulation' },
  { pattern: /\bozone\s+tang\b/gi, replacement: 'acrid tang' },
  { pattern: /\btang\s+of\s+ozone\b/gi, replacement: 'tang of burnt wiring' },
  { pattern: /\bsharpness\s+of\s+ozone\b/gi, replacement: 'acrid sharpness' },
  { pattern: /\bozone[- ]sharp\s+air\b/gi, replacement: 'air sharp with electrical smoke' },
  { pattern: /\bozone[- ]filled\s+air\b/gi, replacement: 'air thick with electrical smoke' },
  { pattern: /\bair\s+tasted\s+of\s+ozone\b/gi, replacement: 'air tasted of electrical smoke' },
  { pattern: /\btaste\s+of\s+ozone\b/gi, replacement: 'taste of electrical smoke' },
  { pattern: /\bthe\s+air\s+was\s+thick\s+with\s+ozone\b/gi, replacement: 'the air was thick with electrical smoke' },
  { pattern: /\belectrical\s+ozone\s+smell\b/gi, replacement: 'electrical burn smell' },
  { pattern: /\bozone[- ]filled\b/gi, replacement: 'thick with electrical smoke' },
  { pattern: /\btasted\s+of\s+ozone\b/gi, replacement: 'tasted of electrical smoke' },
  { pattern: /\bsmell(?:ing|ed)?\s+of\s+ionized\s+air\b/gi, replacement: 'carrying the reek of scorched insulation' },
  { pattern: /\btasted\s+of\s+ionized\s+air\b/gi, replacement: 'tasted of electrical smoke' },
  { pattern: /\bscent\s+of\s+ionized\s+air\b/gi, replacement: 'reek of electrical smoke' },
  { pattern: /\bsmelled\s+like\s+ionized\s+air\b/gi, replacement: 'reeked of scorched insulation' },
  { pattern: /\bozone[- ]tinged\s+scent\b/gi, replacement: 'acrid electrical scent' },
  { pattern: /\bcarried\s+an\s+ozone[- ]tinged\s+scent\b/gi, replacement: 'carried an acrid electrical scent' },
  { pattern: /\bsmell(?:ed|ing|s)?\s+of\b[^.!?]{0,160}?\bozone\b[^.!?]*/gi, replacement: 'carried the reek of scorched insulation and burnt wiring' },
];

const SENSORY_VERB_PATTERN =
  /\b(?:smell(?:ed|ing|s)?|scent(?:ed|s)?|reek(?:ed|ing|s)?|odor|odour|tang(?:ed|s)?|taste[ds]?|tasting)\b/i;
const OZONE_TOKEN_PATTERN = /\bozone(?:[-\w]*)?\b/i;

function splitSentences(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => sentence.trim())
    .filter(Boolean);
}

function isTechnicalOzoneSentence(sentence: string): boolean {
  if (!OZONE_TOKEN_PATTERN.test(sentence)) return true;
  if (!SENSORY_VERB_PATTERN.test(sentence)) return true;
  if (
    /\bozone\s+(?:layer|scrubber|sensor|generator|concentration|concentrations|monitor|monitoring|system|levels?)\b/i.test(
      sentence,
    ) &&
    !/\b(?:smell|scent|reek|odor|odour|tang|taste).{0,100}\bozone\b/i.test(sentence)
  ) {
    return true;
  }
  return false;
}

function findOzoneSensorySentenceMatches(text: string): ForbiddenProseMatch[] {
  const matches: ForbiddenProseMatch[] = [];
  for (const sentence of splitSentences(text || '')) {
    if (isTechnicalOzoneSentence(sentence)) continue;
    if (!OZONE_TOKEN_PATTERN.test(sentence) || !SENSORY_VERB_PATTERN.test(sentence)) continue;
    matches.push({
      kind: 'GLOBAL_CLICHE_PATTERN',
      matchedRuleId: 'ozone_sensory_sentence',
      term: 'ozone sensory sentence',
      count: 1,
    });
  }
  return matches;
}

function dedupeTerms(terms: string[]): string[] {
  const seen = new Set<string>();
  const deduped: string[] = [];
  for (const term of terms) {
    const normalized = term.trim();
    if (!normalized) continue;
    const key = normalized.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(normalized);
  }
  return deduped;
}

function splitProjectForbidden(entries: string[]): { wholeWords: string[]; phrases: string[] } {
  const wholeWords: string[] = [];
  const phrases: string[] = [];
  for (const entry of entries) {
    const trimmed = entry.trim();
    if (!trimmed) continue;
    if (/\s/.test(trimmed)) phrases.push(trimmed);
    else wholeWords.push(trimmed);
  }
  return {
    wholeWords: dedupeTerms(wholeWords),
    phrases: dedupeTerms(phrases),
  };
}

function splitDirectorRestrictions(restrictions: string[]): { wholeWords: string[]; phrases: string[] } {
  return splitProjectForbidden(restrictions);
}

export function getForbiddenProsePolicy(
  bible?: StoryBible | null,
  directorRestrictions: string[] = [],
): ForbiddenProsePolicy {
  const projectEntries = Array.isArray((bible as any)?.styleRules?.forbiddenWordsAndPhrases)
    ? ((bible as any).styleRules.forbiddenWordsAndPhrases as string[])
    : [];
  const projectSplit = splitProjectForbidden(projectEntries);
  const directorSplit = splitDirectorRestrictions(directorRestrictions);

  return {
    globalClichePatterns: GLOBAL_FORBIDDEN_SENSORY_CLICHES,
    projectForbiddenWholeWords: projectSplit.wholeWords,
    projectForbiddenPhrases: projectSplit.phrases,
    directorRestrictions: dedupeTerms([...directorSplit.wholeWords, ...directorSplit.phrases]),
  };
}

/** @deprecated Use getForbiddenProsePolicy for whole-word and phrase lists. */
export function getEffectiveForbiddenProseTerms(
  bible?: StoryBible | null,
  directorRestrictions: string[] = [],
): string[] {
  const policy = getForbiddenProsePolicy(bible, directorRestrictions);
  return dedupeTerms([
    ...policy.projectForbiddenWholeWords,
    ...policy.projectForbiddenPhrases,
    ...policy.directorRestrictions,
  ]);
}

export function findForbiddenProseTerms(text: string, terms: string[]): Array<{ term: string; count: number }> {
  if (!text?.trim() || !terms.length) return [];
  const matches: Array<{ term: string; count: number }> = [];
  for (const term of terms) {
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = /\s/.test(term)
      ? new RegExp(escaped.replace(/\s+/g, '\\s+'), 'gi')
      : new RegExp(`\\b${escaped}\\b`, 'gi');
    const found = text.match(pattern);
    if (found?.length) {
      matches.push({ term, count: found.length });
    }
  }
  return matches;
}

function matchPhrase(text: string, phrase: string, kind: ForbiddenMatchKind, idPrefix: string): ForbiddenProseMatch | null {
  const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(escaped.replace(/\s+/g, '\\s+'), 'gi');
  const found = text.match(pattern);
  if (!found?.length) return null;
  return {
    kind,
    matchedRuleId: `${idPrefix}:${phrase.toLowerCase()}`,
    term: phrase,
    count: found.length,
  };
}

function matchWholeWord(text: string, word: string, kind: ForbiddenMatchKind, idPrefix: string): ForbiddenProseMatch | null {
  const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`\\b${escaped}\\b`, 'gi');
  const found = text.match(pattern);
  if (!found?.length) return null;
  return {
    kind,
    matchedRuleId: `${idPrefix}:${word.toLowerCase()}`,
    term: word,
    count: found.length,
  };
}

export function findForbiddenProseMatches(text: string, policy: ForbiddenProsePolicy): ForbiddenProseMatch[] {
  if (!text?.trim()) return [];
  const matches: ForbiddenProseMatch[] = [];

  for (const sensoryMatch of findOzoneSensorySentenceMatches(text)) {
    matches.push(sensoryMatch);
  }

  for (const cliche of policy.globalClichePatterns) {
    const found = text.match(cliche.pattern);
    if (found?.length) {
      matches.push({
        kind: 'GLOBAL_CLICHE_PATTERN',
        matchedRuleId: cliche.id,
        term: cliche.label,
        count: found.length,
      });
    }
  }

  for (const word of policy.projectForbiddenWholeWords) {
    const match = matchWholeWord(text, word, 'PROJECT_WHOLE_WORD', 'project_word');
    if (match) matches.push(match);
  }

  for (const phrase of policy.projectForbiddenPhrases) {
    const match = matchPhrase(text, phrase, 'PROJECT_PHRASE', 'project_phrase');
    if (match) matches.push(match);
  }

  const directorSplit = splitDirectorRestrictions(policy.directorRestrictions);
  for (const word of directorSplit.wholeWords) {
    const match = matchWholeWord(text, word, 'DIRECTOR_RESTRICTION', 'director_word');
    if (match) matches.push(match);
  }
  for (const phrase of directorSplit.phrases) {
    const match = matchPhrase(text, phrase, 'DIRECTOR_RESTRICTION', 'director_phrase');
    if (match) matches.push(match);
  }

  return matches;
}

export function buildForbiddenProsePrompt(policy: ForbiddenProsePolicy): string {
  const projectWholeWords = policy.projectForbiddenWholeWords.length
    ? policy.projectForbiddenWholeWords.map((term) => `"${term}"`).join(', ')
    : 'none configured';
  const projectPhrases = policy.projectForbiddenPhrases.length
    ? policy.projectForbiddenPhrases.map((term) => `"${term}"`).join(', ')
    : 'none configured';
  const directorRestrictions = policy.directorRestrictions.length
    ? policy.directorRestrictions.map((term) => `"${term}"`).join(', ')
    : 'none active';

  return `
GLOBAL SENSORY-CLICHÉ RULE (MANDATORY):
Do not use ozone as generic sensory shorthand for electricity, magic, storms, machinery, weapons, portals, or damaged electronics.
Avoid clichés such as "the smell of ozone", "smelling of ozone", "the air tasted of ozone", "ozone tang", "ozone-filled air", or any sensory sentence that pairs smell/scent/reek/tang/taste language with ozone (including hyphenated forms like "ozone-scrubbed" or "ozone-tinged").
Use concrete, context-specific sensory details instead, such as scorched insulation, hot copper, burnt wiring, singed oil, electrical smoke, melted plastic, or acrid lubricant fumes.
Do not substitute "ionized air" or similar clichés that mean the same thing as ozone shorthand.

Literal scientific, environmental, medical, or industrial references to ozone are permitted when required by the story
(for example: ozone layer, ozone concentrations, ozone scrubber, ozone sensor, ozone generator).

Also avoid ionized-air sensory shorthand such as "the smell of ionized air" when it is only standing in for electrical burn clichés.

PROJECT FORBIDDEN WHOLE WORDS: ${projectWholeWords}
PROJECT FORBIDDEN PHRASES: ${projectPhrases}
DIRECTOR RESTRICTIONS: ${directorRestrictions}
`.trim();
}

export function buildEffectiveProseInstruction(
  bible?: StoryBible | null,
  directorRestrictions: string[] = [],
): string {
  return buildForbiddenProsePrompt(getForbiddenProsePolicy(bible, directorRestrictions));
}

export function applyDeterministicForbiddenProseFallback(text: string): string {
  let result = text;
  for (const { pattern, replacement } of CLICHE_REPLACEMENTS) {
    result = result.replace(pattern, replacement);
  }
  return result;
}

export function logForbiddenProseDiagnostic(
  tag: '[FORBIDDEN PROSE TERM DETECTED]' | '[FORBIDDEN PROSE TERM REPAIR]' | '[FORBIDDEN PROSE FINAL CHECK]',
  payload: {
    matchKind?: ForbiddenMatchKind;
    matchedRuleId?: string;
    agentStage: string;
    occurrenceCount?: number;
    repaired?: boolean;
    remainingCount?: number;
    term?: string;
  },
): void {
  console.log(`${tag} ${JSON.stringify(payload)}`);
}

export function validateForbiddenProseForPersistence(
  text: string | undefined,
  bible?: StoryBible | null,
  directorRestrictions: string[] = [],
): { ok: true } | { ok: false; message: string; matches: ForbiddenProseMatch[] } {
  const policy = getForbiddenProsePolicy(bible, directorRestrictions);
  const matches = findForbiddenProseMatches(text || '', policy);
  logForbiddenProseDiagnostic('[FORBIDDEN PROSE FINAL CHECK]', {
    agentStage: 'persistence',
    occurrenceCount: matches.reduce((sum, item) => sum + item.count, 0),
    remainingCount: matches.length,
    matchKind: matches[0]?.kind,
    matchedRuleId: matches[0]?.matchedRuleId,
    term: matches[0]?.term,
  });
  if (!matches.length) return { ok: true };
  return {
    ok: false,
    message: `Chapter contains forbidden prose (${matches[0].kind}: ${matches[0].term}) and cannot be persisted.`,
    matches,
  };
}

export async function enforceForbiddenProseText(params: {
  text: string;
  bible?: StoryBible | null;
  agentStage: string;
  directorRestrictions?: string[];
  repairSentence?: (sentence: string) => Promise<string>;
}): Promise<ForbiddenProseEnforcementResult> {
  const policy = getForbiddenProsePolicy(params.bible, params.directorRestrictions || []);
  let text = params.text || '';
  let repaired = false;

  const initial = findForbiddenProseMatches(text, policy);
  if (!initial.length) {
    return { text, blocked: false, repaired: false, remaining: [], agentStage: params.agentStage };
  }

  logForbiddenProseDiagnostic('[FORBIDDEN PROSE TERM DETECTED]', {
    agentStage: params.agentStage,
    term: initial[0]?.term,
    matchKind: initial[0]?.kind,
    matchedRuleId: initial[0]?.matchedRuleId,
    occurrenceCount: initial.reduce((sum, item) => sum + item.count, 0),
  });

  const deterministic = applyDeterministicForbiddenProseFallback(text);
  if (deterministic !== text) {
    text = deterministic;
    repaired = true;
    const remainingAfter = findForbiddenProseMatches(text, policy);
    logForbiddenProseDiagnostic('[FORBIDDEN PROSE TERM REPAIR]', {
      agentStage: params.agentStage,
      term: initial[0]?.term,
      matchKind: initial[0]?.kind,
      matchedRuleId: initial[0]?.matchedRuleId,
      repaired: true,
      remainingCount: remainingAfter.length,
    });
  }

  let remaining = findForbiddenProseMatches(text, policy);
  if (remaining.length && params.repairSentence) {
    const sentences = text.split(/(?<=[.!?])\s+/);
    const nextSentences: string[] = [];
    for (const sentence of sentences) {
      if (!findForbiddenProseMatches(sentence, policy).length) {
        nextSentences.push(sentence);
        continue;
      }
      const repairedSentence = await params.repairSentence(sentence);
      nextSentences.push(repairedSentence);
      repaired = true;
    }
    text = nextSentences.join(' ');
    remaining = findForbiddenProseMatches(text, policy);
    logForbiddenProseDiagnostic('[FORBIDDEN PROSE TERM REPAIR]', {
      agentStage: params.agentStage,
      term: remaining[0]?.term || initial[0]?.term,
      matchKind: remaining[0]?.kind || initial[0]?.kind,
      matchedRuleId: remaining[0]?.matchedRuleId || initial[0]?.matchedRuleId,
      repaired: true,
      remainingCount: remaining.length,
    });
  }

  if (!remaining.length) {
    return { text, blocked: false, repaired, remaining: [], agentStage: params.agentStage };
  }

  const fallback = applyDeterministicForbiddenProseFallback(text);
  remaining = findForbiddenProseMatches(fallback, policy);
  return {
    text: remaining.length ? text : fallback,
    blocked: remaining.length > 0,
    repaired: repaired || fallback !== params.text,
    remaining,
    agentStage: params.agentStage,
  };
}
